const terrainPath = (baseUrl, path) => `${String(baseUrl || '').replace(/\/$/, '')}/api/v1/terrain${path}`;

// El backend de Terreno exige un header X-CSRF-Token en toda mutación,
// emitido en el header X-Csrf-Token de cualquier respuesta autenticada
// previa (auth/terrain_session.go). csrfToken guarda el último valor visto
// para reenviarlo en las siguientes mutaciones; el interceptor de errores
// reintenta una única vez si el primer intento cae por token ausente
// (por ejemplo, la primera mutación de la sesión, sin ningún GET previo).
let csrfToken = null;
let csrfInterceptorRegistered = false;

function captureCsrfToken(headers) {
  const token = headers && headers['x-csrf-token'];
  if (token) csrfToken = token;
}

// createIdempotencyKey generates a client-side idempotency token for
// mutations the backend requires one for (publish, close), mirroring the
// same fallback used by AutosaveController.vue.
function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `terrain-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function registerCsrfInterceptor(http) {
  if (csrfInterceptorRegistered) return;
  if (!http.interceptors || typeof http.interceptors.response.use !== 'function') return;
  csrfInterceptorRegistered = true;

  http.interceptors.response.use(
    (response) => {
      captureCsrfToken(response.headers);
      return response;
    },
    (error) => {
      const response = error.response;
      if (!response) return Promise.reject(error);
      captureCsrfToken(response.headers);

      const config = error.config;
      const isCsrfRejection = response.status === 403 && response.data && response.data.code === 'TERRAIN_CSRF_INVALID';
      if (isCsrfRejection && csrfToken && config && !config.__terrainCsrfRetried) {
        config.__terrainCsrfRetried = true;
        config.headers = { ...config.headers, 'X-CSRF-Token': csrfToken };
        return http.request(config);
      }
      return Promise.reject(error);
    },
  );
}

/**
 * Creates the authenticated Terrain HTTP adapter. Keeping draft transport here
 * makes controllers independent from Axios and ensures every save carries the
 * concurrency and idempotency headers required by the API.
 */
export function createTerrainApi(http, baseUrl = '') {
  if (!http) throw new Error('Terrain API requires an HTTP client.');
  registerCsrfInterceptor(http);

  return {
    getDraft(campaignId, prefillCaseId = '') {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, `/campaigns/${campaignId}/draft`), { withCredentials: true, params: prefillCaseId ? { prefillCaseId } : {} });
    },
    saveDraft(campaignId, draft, resourceVersion, idempotencyKey) {
      if (typeof http.put !== 'function') throw new Error('Terrain API requires an HTTP put method.');
      return http.put(terrainPath(baseUrl, `/campaigns/${campaignId}/draft`), draft, {
        withCredentials: true,
        headers: {
          'If-Match': `"${resourceVersion}"`,
          'Idempotency-Key': idempotencyKey,
          'X-CSRF-Token': csrfToken,
        },
      });
    },
    // submitResponse implementa el envío final del Respondente
    // (POST /campaigns/:id/submissions). El backend no exige If-Match acá
    // (no hay resourceVersion previo: la Respuesta recién se crea), solo
    // Idempotency-Key para que un reintento de red no duplique el envío.
    submitResponse(campaignId, payload, idempotencyKey) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/submissions`), payload, {
        withCredentials: true,
        headers: {
          'X-CSRF-Token': csrfToken,
          'Idempotency-Key': idempotencyKey || createIdempotencyKey(),
        },
      });
    },
    listResponses(params) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, '/responses'), { withCredentials: true, params });
    },
    getResponse(responseId) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, `/responses/${responseId}`), { withCredentials: true });
    },
    transitionResponse(responseId, transition, resourceVersion, idempotencyKey) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, `/responses/${responseId}/transitions`), transition, {
        withCredentials: true,
        headers: {
          'If-Match': `"${resourceVersion}"`,
          'Idempotency-Key': idempotencyKey,
          'X-CSRF-Token': csrfToken,
        },
      });
    },
    correctResponse(responseId, correction, resourceVersion, idempotencyKey) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, `/responses/${responseId}/corrections`), correction, {
        withCredentials: true,
        headers: {
          'If-Match': `"${resourceVersion}"`,
          'Idempotency-Key': idempotencyKey,
          'X-CSRF-Token': csrfToken,
        },
      });
    },
    listAuditEvents(params) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, '/audit-events'), { withCredentials: true, params });
    },
    listTemplates({ page, size, includeArchived } = {}) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, '/templates'), { withCredentials: true, params: { page, size, includeArchived } });
    },
    validateTemplate(templateId) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, `/templates/${templateId}/validate`), {}, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    publishTemplate(templateId) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, `/templates/${templateId}/publish`), {}, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken, 'Idempotency-Key': createIdempotencyKey() },
      });
    },
    archiveTemplate(templateId) {
      if (typeof http.put !== 'function') throw new Error('Terrain API requires an HTTP put method.');
      return http.put(terrainPath(baseUrl, `/templates/${templateId}/archive`), {}, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    restoreTemplate(templateId) {
      if (typeof http.put !== 'function') throw new Error('Terrain API requires an HTTP put method.');
      return http.put(terrainPath(baseUrl, `/templates/${templateId}/restore`), {}, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    listTemplateVersions(templateId, { page, size } = {}) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, `/templates/${templateId}/versions`), { withCredentials: true, params: { page, size } });
    },
    getTemplateVersion(versionId) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, `/template-versions/${versionId}`), { withCredentials: true });
    },
    createTemplate({ title, description } = {}) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, '/templates'), { title, description }, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    getTemplate(templateId) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, `/templates/${templateId}`), { withCredentials: true });
    },
    // updateTemplateStructure applies a single structural operation to the
    // template draft. The backend implements optimistic concurrency via
    // `expectedVersion` inside the JSON body (not an `If-Match` header), so
    // structureRequest must already include `op`/`expectedVersion` alongside
    // whichever fields the operation needs (see dto/terrain/template_ops.go).
    updateTemplateStructure(templateId, structureRequest) {
      if (typeof http.put !== 'function') throw new Error('Terrain API requires an HTTP put method.');
      return http.put(terrainPath(baseUrl, `/templates/${templateId}`), structureRequest, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    listCampaigns({ page, size } = {}) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, '/campaigns'), { withCredentials: true, params: { page, size } });
    },
    getCampaign(campaignId) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, `/campaigns/${campaignId}`), { withCredentials: true });
    },
    createCampaign(payload) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, '/campaigns'), payload, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    updateCampaign(campaignId, payload) {
      if (typeof http.put !== 'function') throw new Error('Terrain API requires an HTTP put method.');
      return http.put(terrainPath(baseUrl, `/campaigns/${campaignId}`), payload, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    // publishCampaign requires both If-Match (current resourceVersion) and
    // Idempotency-Key (handlers/terrain/campaign_publish.go): retrying the
    // same key must return the already-published campaign, not publish it
    // twice.
    publishCampaign(campaignId, resourceVersion) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/publish`), {}, {
        withCredentials: true,
        headers: {
          'X-CSRF-Token': csrfToken,
          'If-Match': `"${resourceVersion}"`,
          'Idempotency-Key': createIdempotencyKey(),
        },
      });
    },
    closeCampaign(campaignId, resourceVersion) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/close`), {}, {
        withCredentials: true,
        headers: {
          'X-CSRF-Token': csrfToken,
          'If-Match': `"${resourceVersion}"`,
        },
      });
    },
    // deleteCampaign oculta la campaña (soft delete, cualquier status) - ver
    // handlers/terrain/campaign_lifecycle.go::SoftDeleteCampaign. Si estaba
    // publicada, su Capa_Terreno desaparece de inmediato de todos los mapas.
    deleteCampaign(campaignId, resourceVersion) {
      if (typeof http.delete !== 'function') throw new Error('Terrain API requires an HTTP delete method.');
      return http.delete(terrainPath(baseUrl, `/campaigns/${campaignId}`), {
        withCredentials: true,
        headers: {
          'X-CSRF-Token': csrfToken,
          'If-Match': `"${resourceVersion}"`,
        },
      });
    },
    // hardDeleteCampaign la borra en definitiva - el backend rechaza con
    // TERRAIN_CAMPAIGN_HAS_RESPONSES si ya tiene alguna respuesta enviada.
    hardDeleteCampaign(campaignId, resourceVersion) {
      if (typeof http.delete !== 'function') throw new Error('Terrain API requires an HTTP delete method.');
      return http.delete(terrainPath(baseUrl, `/campaigns/${campaignId}/permanent`), {
        withCredentials: true,
        headers: {
          'X-CSRF-Token': csrfToken,
          'If-Match': `"${resourceVersion}"`,
        },
      });
    },
    listCrews({ page, size, search, active } = {}) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      const params = { page, size };
      if (search) params.search = search;
      if (active !== undefined && active !== null) params.active = active;
      return http.get(terrainPath(baseUrl, '/crews'), { withCredentials: true, params });
    },
    getCrew(crewId) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, `/crews/${crewId}`), { withCredentials: true });
    },
    createCrew(payload) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, '/crews'), payload, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    updateCrew(crewId, payload) {
      if (typeof http.put !== 'function') throw new Error('Terrain API requires an HTTP put method.');
      return http.put(terrainPath(baseUrl, `/crews/${crewId}`), payload, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    updateCrewStatus(crewId, payload) {
      if (typeof http.put !== 'function') throw new Error('Terrain API requires an HTTP put method.');
      return http.put(terrainPath(baseUrl, `/crews/${crewId}/status`), payload, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    // getRecipientSources sends ids as comma-joined query params (the
    // backend also accepts repeated params, but axios's default array
    // serialization emits `key[]=` which Gin's QueryArray would not match —
    // see handlers/terrain/recipient_sources.go parseCommaSeparatedIDs).
    getRecipientSources({
	  emails = [], excludedEmails = [], userIds = [], groupIds = [], crewIds = [], excludedUserIds = [],
    } = {}) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      const params = {};
	  if (emails.length) params.emails = emails.join(',');
	  if (excludedEmails.length) params.excludedEmails = excludedEmails.join(',');
      if (userIds.length) params.userIds = userIds.join(',');
      if (groupIds.length) params.groupIds = groupIds.join(',');
      if (crewIds.length) params.crewIds = crewIds.join(',');
      if (excludedUserIds.length) params.excludedUserIds = excludedUserIds.join(',');
      return http.get(terrainPath(baseUrl, '/recipient-sources'), { withCredentials: true, params });
    },
	getPublicCampaignInfo(publicAccessId) {
	  return http.get(terrainPath(baseUrl, `/public/campaigns/${publicAccessId}`), { withCredentials: true });
	},
	createPublicSession(publicAccessId, email) {
	  return http.post(terrainPath(baseUrl, `/public/campaigns/${publicAccessId}/session`), { email }, { withCredentials: true });
	},
	createGeneralControlledSession(publicAccessId, email, key) {
	  return http.post(terrainPath(baseUrl, `/public/campaigns/${publicAccessId}/controlled-session`), { email, key }, { withCredentials: true });
	},
	createControlledSession(recipientAccessId, email, key) {
	  return http.post(terrainPath(baseUrl, `/public/recipients/${recipientAccessId}/session`), { email, key }, { withCredentials: true });
	},
	resolveControlledForSession(recipientAccessId) { return http.get(terrainPath(baseUrl, `/recipient-links/${recipientAccessId}`), { withCredentials: true }); },
	listPrefillInbox(campaignId) {
	  return http.get(terrainPath(baseUrl, `/campaigns/${campaignId}/prefill/inbox`), { withCredentials: true });
	},
	reservePrefillCase(campaignId, caseId) {
	  return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/prefill/cases/${caseId}/reserve`), {}, { withCredentials: true, headers: { 'X-CSRF-Token': csrfToken } });
	},
	releasePrefillCase(campaignId, caseId) {
	  return http.delete(terrainPath(baseUrl, `/campaigns/${campaignId}/prefill/cases/${caseId}/reservation`), { withCredentials: true, headers: { 'X-CSRF-Token': csrfToken } });
	},
	listPrefillCases(campaignId, page = 1, size = 100) {
	  return http.get(terrainPath(baseUrl, `/campaigns/${campaignId}/prefill/cases`), { withCredentials: true, params: { page, size } });
	},
	adminReleasePrefillCase(campaignId, caseId) {
	  return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/prefill/cases/${caseId}/release`), {}, { withCredentials: true, headers: { 'X-CSRF-Token': csrfToken } });
	},
	previewPrefill(campaignId, rows) {
	  return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/prefill/preview`), { rows }, { withCredentials: true, headers: { 'X-CSRF-Token': csrfToken } });
	},
	confirmPrefill(campaignId, rows, fieldPolicies = []) {
	  return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/prefill/confirm`), { rows, fieldPolicies }, { withCredentials: true, headers: { 'X-CSRF-Token': csrfToken } });
	},
	previewPrefillFile(campaignId, file, sheet = '') {
	  const data = new FormData(); data.append('file', file); if (sheet) data.append('sheet', sheet);
	  return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/prefill/file-preview`), data, { withCredentials: true, headers: { 'X-CSRF-Token': csrfToken } });
	},
	downloadPrefillBase(campaignId) { return http.get(terrainPath(baseUrl, `/campaigns/${campaignId}/prefill/base.csv`), { withCredentials: true, responseType: 'blob' }); },
	deletePrefillCase(campaignId, caseId) { return http.delete(terrainPath(baseUrl, `/campaigns/${campaignId}/prefill/cases/${caseId}`), { withCredentials: true, headers: { 'X-CSRF-Token': csrfToken } }); },
	updatePrefillCase(campaignId, caseId, answers, fieldPolicies = []) {
	  return http.put(terrainPath(baseUrl, `/campaigns/${campaignId}/prefill/cases/${caseId}`), { answers, fieldPolicies }, { withCredentials: true, headers: { 'X-CSRF-Token': csrfToken } });
	},
	listCredentials(campaignId) { return http.get(terrainPath(baseUrl, `/campaigns/${campaignId}/credentials`), { withCredentials: true }); },
	generateCredentials(campaignId) { return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/credentials/generate`), {}, { withCredentials: true, headers: { 'X-CSRF-Token': csrfToken } }); },
	regenerateCredential(campaignId, recipientId) { return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/credentials/${recipientId}/regenerate`), {}, { withCredentials: true, headers: { 'X-CSRF-Token': csrfToken } }); },
	exchangeInvitation(token) {
	  return http.post(terrainPath(baseUrl, '/public/invitations/exchange'), { token }, { withCredentials: true });
	},
	deletePublicSession() {
	  return http.delete(terrainPath(baseUrl, '/public/session'), { withCredentials: true, headers: { 'X-CSRF-Token': csrfToken } });
	},
	getCorrection(campaignId, responseId) {
	  return http.get(terrainPath(baseUrl, `/campaigns/${campaignId}/responses/${responseId}/correction`), { withCredentials: true });
	},
	getCorrectionDraft(campaignId, responseId) {
	  return http.get(terrainPath(baseUrl, `/campaigns/${campaignId}/responses/${responseId}/correction-draft`), { withCredentials: true });
	},
	saveCorrectionDraft(campaignId, responseId, draft, resourceVersion, idempotencyKey) {
	  return http.put(terrainPath(baseUrl, `/campaigns/${campaignId}/responses/${responseId}/correction-draft`), draft, { withCredentials: true, headers: { 'If-Match': `"${resourceVersion}"`, 'Idempotency-Key': idempotencyKey || createIdempotencyKey(), 'X-CSRF-Token': csrfToken } });
	},
	submitCorrection(campaignId, responseId, payload, resourceVersion, idempotencyKey) {
	  return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/responses/${responseId}/corrections`), payload, { withCredentials: true, headers: { 'If-Match': `"${resourceVersion}"`, 'Idempotency-Key': idempotencyKey || createIdempotencyKey(), 'X-CSRF-Token': csrfToken } });
	},
    // getMapSection devuelve el descriptor de Sección Terreno (tarea 13.2):
    // las Capas_Terreno asignadas a este mapa, ya en el orden administrativo
    // total (visualGroup.order, visualGroup.id, layerOrder, terrainLayerId).
    getMapSection(mapId) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, `/maps/${mapId}/section`), { withCredentials: true });
    },
    // updateMapLayerSettings implementa PUT /layers/:id/maps/:mapId (tarea
    // 13.1): único punto de escritura de terrain_map_layer_settings — grupo
    // visual, orden, visibilidad inicial y estilo de UNA capa en UN mapa.
    updateMapLayerSettings(terrainLayerId, mapId, payload) {
      if (typeof http.put !== 'function') throw new Error('Terrain API requires an HTTP put method.');
      return http.put(terrainPath(baseUrl, `/layers/${terrainLayerId}/maps/${mapId}`), payload, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    // resolveGeoIntersectIds implementa PB-24_v3/v4: resuelve el filtro
    // espacial entre-capas de una Capa_Terreno directo contra MongoDB
    // ($geoIntersects/$geoWithin nativo), evitando el traductor CQL_FILTER
    // de GeoServer/gs-mongodb. geometries es una lista de GeoJSON
    // Polygon/MultiPolygon en EPSG:4326 (partes de la capa objetivo, ya
    // reparadas por-feature pero SIN unir entre sí — PB-24_v4, el backend
    // las empaqueta en un solo MultiPolygon); operation es 'intersects' o
    // 'within'.
    resolveGeoIntersectIds(mapId, layerId, geometries, operation) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, `/maps/${mapId}/layers/${layerId}/geo-intersect-ids`), { geometries, operation }, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    // listRecipients devuelve el roster completo (activos y retirados) de
    // una campaña, usado para ofrecer el Retiro_Administrativo_de_
    // Destinatario (tarea 4.6/4.7).
    listRecipients(campaignId) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, `/campaigns/${campaignId}/recipients`), { withCredentials: true });
    },
    // getNewMembers implementa la tarea 4.4: usuarios activos de los
    // grupos/cuadrillas seleccionados de una campaña publicada que todavía
    // no pertenecen a su lista de destinatarios.
    getNewMembers(campaignId) {
      if (typeof http.get !== 'function') throw new Error('Terrain API requires an HTTP get method.');
      return http.get(terrainPath(baseUrl, `/campaigns/${campaignId}/new-members`), { withCredentials: true });
    },
    // expandRecipients implementa Enviar_a_Nuevos_Integrantes (tarea 4.5):
    // Idempotency-Key evita duplicar incorporaciones ante un reintento de
    // red.
    expandRecipients(campaignId) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/recipients/expand`), {}, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken, 'Idempotency-Key': createIdempotencyKey() },
      });
    },
    // withdrawRecipient implementa el Retiro_Administrativo_de_Destinatario
    // (tarea 4.6): reason es obligatorio en el backend.
    withdrawRecipient(campaignId, userId, reason) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, `/campaigns/${campaignId}/recipients/${userId}/withdraw`), { reason }, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    // uploadAttachment implementa el Proceso 1 (POST /attachments,
    // multipart/form-data). formData ya debe traer campaignId,
    // templateVersionId, questionId, kind y file — no se setea Content-Type
    // a mano: axios detecta el FormData y arma el boundary solo, mismo
    // criterio que restDocument.js::postDoc.
    uploadAttachment(formData) {
      if (typeof http.post !== 'function') throw new Error('Terrain API requires an HTTP post method.');
      return http.post(terrainPath(baseUrl, '/attachments'), formData, {
        withCredentials: true,
        headers: { 'X-CSRF-Token': csrfToken },
      });
    },
    // getAttachmentContentUrl arma la URL de descarga protegida (Proceso 2)
    // para usar directo en <img>/<a> — el navegador manda la cookie de
    // sesión sola, sin necesidad de un fetch manual.
    getAttachmentContentUrl(attachmentId) {
      return terrainPath(baseUrl, `/attachments/${attachmentId}/content`);
    },
  };
}
